import React, { useState } from 'react';
import { Field, useFormikContext } from 'formik';
import {
    Checkbox,
    Typography,
    FormControl,
    FormGroup,
    FormHelperText,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Tooltip,
} from '@mui/material';
import { PhoneInputField, YesNoSelector } from '@fnol/library/src/components';
import { maxCharacterLimit, paymentOptions } from '@fnol/library/src/lib';
import { constValues, translations, question150MaxLength, question255MaxLength } from '../../lib/consts';
import { removeEmptyProperties, showPersonsWithPhoneNumber } from '../../lib/helpers';

const DamagedVehicleSection = () => {
    const { values, touched, errors, setFieldValue } = useFormikContext<any>();
    const [isSelected, setIsSelected] = useState<any>();

    const onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            if (!isSelected) {
                setFieldValue('payment', e.target.name);
                setIsSelected(e.target.name);
            }
        } else {
            setFieldValue('payment', '');
            setIsSelected(null);
        }
    };

    const INDIVIDUALS_OPTIONS = [
        {
            id: 1,
            value: `${values.submitterIndividualName} ${values.submitterIndividualLastName}`,
            phoneNumber: values.submitterIndividualPhone,
        },
        {
            id: 2,
            value: `${values.ownerIndividualName} ${values.ownerIndividualLastName}`,
            phoneNumber: values.ownerIndividualPhone,
        },
        {
            id: 3,
            value: `${values.userIndividualName} ${values.userIndividualLastName}`,
            phoneNumber: values.userIndividualPhone,
        },
        {
            id: 4,
            value: `${values.submitterLegalEntityContactName} ${values.submitterLegalEntityContactLastName}`,
            phoneNumber: values.submitterLegalEntityContactPhone,
        },
        {
            id: 5,
            value: `${values.driverIndividualName} ${values.driverIndividualLastName}`,
            phoneNumber: values.driverIndividualPhone,
        },
    ];

    const filterEmptyValuesIndividuals = removeEmptyProperties(INDIVIDUALS_OPTIONS);
    const individualsWithDomesticPhoneNumber = showPersonsWithPhoneNumber(filterEmptyValuesIndividuals);

    const handleChangeSendLink = (e: { target: { value: any } }): any => {
        setFieldValue('sendLink', e.target.value);
        setFieldValue('vehicleDamageMobile', constValues.dialCodeSerbia);
    };

    const handleChangeSmsLink = () => {
        setFieldValue('vehicleDamageMobile', constValues.dialCodeSerbia);
        setFieldValue('sendLink', '');
    };

    const handleChangeIsVehicleDrivable = () => {
        setFieldValue('vehicleLocation', '');
    };

    return (
        <>
            <Typography style={{ fontWeight: 'bold', fontSize: '18px', paddingTop: '16px' }} variant="subtitle1">
                {translations.sectionDamageVehicle.DAMAGE_DATA}
            </Typography>
            <Typography variant="subtitle1">{translations.sectionDamageVehicle.IS_VEHICLE_DRIVABLE}</Typography>
            <FormControl>
                <YesNoSelector name="isVehicleDrivable" onChange={handleChangeIsVehicleDrivable} />
                {values.isVehicleDrivable === 'false' && (
                    <>
                        <Field
                            name="vehicleLocation"
                            label={translations.sectionDamageVehicle.VEHICLE_LOCATION}
                            as={TextField}
                            required={values.isVehicleDrivable === 'false'}
                            inputProps={{ maxLength: question255MaxLength }}
                            error={!!(touched.vehicleLocation && errors.vehicleLocation)}
                        />
                        {touched.vehicleLocation && errors.vehicleLocation ? (
                            <FormHelperText error={true}>
                                <>{errors.vehicleLocation}</>
                            </FormHelperText>
                        ) : null}
                    </>
                )}
            </FormControl>
            <FormControl>
                <Field
                    name="vehicleDamageDescription"
                    multiline={true}
                    label={translations.sectionDamageVehicle.DAMAGE_DESCRIPTION}
                    as={TextField}
                    maxRows={5}
                    minRows={5}
                    inputProps={{
                        maxLength: maxCharacterLimit,
                    }}
                    value={values.vehicleDamageDescription}
                    helperText={`${translations.REMAINING_CHARACTERS} ${
                        maxCharacterLimit - values.vehicleDamageDescription.length
                    }`}
                />
                {touched.vehicleDamageDescription && errors.vehicleDamageDescription ? (
                    <FormHelperText error={true}>
                        <>{errors.vehicleDamageDescription}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
            <Tooltip title={translations.sectionDamageVehicle.OTHER_DAMAGES_TOOLTIP} placement="top-start">
                <FormControl>
                    <Field
                        name="vehicleOtherDamages"
                        multiline={true}
                        label={translations.sectionDamageVehicle.OTHER_DAMAGE}
                        as={TextField}
                        maxRows={5}
                        minRows={5}
                        inputProps={{
                            maxLength: maxCharacterLimit,
                        }}
                        value={values.vehicleOtherDamages}
                        helperText={`${translations.REMAINING_CHARACTERS} ${
                            maxCharacterLimit - values.vehicleOtherDamages.length
                        }`}
                    />
                </FormControl>
            </Tooltip>
            <Typography style={{ fontWeight: 'bold', fontSize: '18px', paddingTop: '16px' }} variant="subtitle1">
                {translations.sectionDamageVehicle.SERVICE}
            </Typography>
            <FormControl>
                <Field
                    name="vehicleService"
                    label={translations.sectionDamageVehicle.SERVICE_NAME_AND_LOCATION}
                    as={TextField}
                    inputProps={{
                        maxLength: question150MaxLength,
                    }}
                />
            </FormControl>
            <Typography style={{ fontWeight: 'bold', fontSize: '18px', paddingTop: '16px' }} variant="subtitle1">
                {translations.sectionDamageVehicle.PAYMENT}
            </Typography>
            <Typography variant="subtitle1">{translations.sectionDamageVehicle.WHO_SHOULD_WE_PAY}</Typography>
            <FormControl component="fieldset">
                <FormGroup>
                    {paymentOptions.map((opt: any) => {
                        return (
                            <label
                                key={opt.value}
                                className="service-label"
                                style={{ marginLeft: '-10px', marginTop: '-10px' }}
                            >
                                <Checkbox
                                    name={opt.value}
                                    key={opt.label}
                                    checked={values.payment == opt.value ? true : false}
                                    className="checkbox"
                                    disabled={isSelected ? isSelected != opt.value : false}
                                    onChange={onCheckboxChange}
                                />
                                {opt.label}
                            </label>
                        );
                    })}
                </FormGroup>
            </FormControl>
            <Typography variant="subtitle1" style={{ fontWeight: 'bold', fontSize: '18px', paddingTop: '16px' }}>
                {translations.sectionDamageVehicle.DAMAGE_PHOTOS}
            </Typography>
            <Typography variant="subtitle1">{translations.sectionDamageVehicle.SMS_LINK}</Typography>
            <YesNoSelector name="smsLink" onChange={handleChangeSmsLink} />
            {values.smsLink === 'true' && (
                <>
                    <FormControl>
                        <InputLabel id="send-link-label">
                            {translations.sectionDamageVehicle.WHO_SHOULD_WE_SEND_LINK}
                        </InputLabel>

                        <Select
                            id="send-link"
                            labelId="send-link-label"
                            name="sendLink"
                            value={values.sendLink || ''}
                            label={translations.sectionDamageVehicle.WHO_SHOULD_WE_SEND_LINK}
                            onChange={handleChangeSendLink}
                            error={!!(touched.sendLink && errors.sendLink)}
                        >
                            {individualsWithDomesticPhoneNumber.map((option: any) => {
                                return (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.value}
                                    </MenuItem>
                                );
                            })}
                            <MenuItem key={6} value={6}>
                                {translations.ADD_CONTACT}
                            </MenuItem>
                        </Select>
                        {touched.sendLink && errors.sendLink ? (
                            <FormHelperText error={true}>
                                <>{errors.sendLink}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>
                </>
            )}
            {values.sendLink === 6 && (
                <Tooltip title={<p>{translations.tooltips.PHONE_TOOLTIP}</p>} placement="top-start">
                    <FormControl>
                        <PhoneInputField
                            name="vehicleDamageMobile"
                            country={'rs'}
                            specialLabel={translations.personalInfo.MOBILE_STAR}
                        />
                    </FormControl>
                </Tooltip>
            )}
        </>
    );
};

export default DamagedVehicleSection;
